import React, { useState, useMemo, useEffect } from 'react';
import { User } from 'firebase/auth';
import { 
  Sparkles, 
  ArrowRight, 
  Zap, 
  ShieldCheck, 
  Lock, 
  Receipt, 
  CreditCard, 
  BarChart3, 
  Brain, 
  Bot, 
  Building2, 
  FileCheck, 
  Target, 
  ExternalLink, 
  RefreshCw, 
  Sliders, 
  Check, 
  TrendingUp, 
  Calendar, 
  CheckCircle2, 
  HelpCircle, 
  Download, 
  FileText, 
  AlertCircle,
  Mail,
  ChevronRight,
  Globe,
  DollarSign,
  Scale,
  Users,
  Award,
  Clock,
  Landmark,
  Calculator,
  Play
} from 'lucide-react';
import { GoogleCloudPartnerBadge } from './GoogleCloudPartnerBadge';
import { RequestDemoModal, DemoInquiryType } from './RequestDemoModal';
import { InteractiveTourModal } from './InteractiveTourModal';
import { WhatIsAzuraOSModal, PILOT_HIGHLIGHTS } from './WhatIsAzuraOSModal';
import { MarginDiagnosticEmailModal, MarginDiagnosticData } from './MarginDiagnosticEmailModal';
import { QuickStartWizard } from './QuickStartWizard';
import { SEO } from './SEO';
import { getBusinessSettings } from '../utils/businessSettings';
import { getJurisdictionPreset, JURISDICTION_PRESETS } from '../utils/jurisdictionPresets';
import { FounderCrisisPromptDeck } from './FounderCrisisPromptDeck';

interface LandingViewProps {
  onGetStarted: () => void;
  marginThreshold: number;
  onOpenAuthModal?: (prefilledEmail?: string) => void;
  currentUser?: User | null;
  onOpenInviteModal?: () => void;
  onOpenTerms?: () => void;
  onOpenPrivacy?: () => void;
  onOpenSecurity?: () => void;
  onOpenFAQ?: () => void;
  onOpenRefundPolicy?: () => void;
  onOpenContact?: () => void;
  onOpenWhatIsAzuraOS?: () => void;
  onOpenQuickStart?: () => void;
  onLoadDemo?: () => void;
  onClearSandbox?: () => void;
  onOpenReconciler?: () => void;
  onOpenCohortPortal?: () => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
  onTourStateChange?: (isOpen: boolean) => void;
  initialAudienceMode?: 'solo' | 'cohort';
}

export function LandingView({ 
  onGetStarted, 
  marginThreshold, 
  onOpenAuthModal, 
  currentUser, 
  onOpenInviteModal,
  onOpenTerms,
  onOpenPrivacy,
  onOpenSecurity,
  onOpenFAQ,
  onOpenRefundPolicy,
  onOpenContact,
  onOpenWhatIsAzuraOS,
  onOpenQuickStart,
  onLoadDemo,
  onClearSandbox,
  onOpenReconciler,
  onOpenCohortPortal,
  showToast,
  onTourStateChange,
  initialAudienceMode
}: LandingViewProps) {
  // Modal States
  const [isQuickStartOpen, setIsQuickStartOpen] = useState<boolean>(false);
  const [isRequestDemoOpen, setIsRequestDemoOpen] = useState<boolean>(false);
  const [isInteractiveTourOpen, setIsInteractiveTourOpen] = useState<boolean>(false);
  const [isWhatIsAzuraOSModalOpen, setIsWhatIsAzuraOSModalOpen] = useState<boolean>(false);
  const [whatIsAzuraOSTab, setWhatIsAzuraOSTab] = useState<'pitch' | 'capabilities' | 'pilot_program'>('pitch');
  const [isDiagnosticModalOpen, setIsDiagnosticModalOpen] = useState<boolean>(false);
  const [audienceMode, setAudienceMode] = useState<'solo' | 'cohort'>(() => {
    if (initialAudienceMode) return initialAudienceMode;
    try {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.toLowerCase();
        if (path === '/cohorts' || path === '/accelerators' || path === '/incubators' || path === '/enterprise') {
          return 'cohort';
        }
        const params = new URLSearchParams(window.location.search);
        const mode = (params.get('audience') || params.get('mode') || params.get('tab') || params.get('view') || '').toLowerCase();
        if (mode === 'cohort' || mode === 'cohorts' || mode === 'accelerator' || mode === 'accelerators' || mode === 'incubator') {
          return 'cohort';
        }
      }
    } catch {
      // Ignore in SSR
    }
    return 'solo';
  });

  useEffect(() => {
    if (initialAudienceMode) {
      setAudienceMode(initialAudienceMode);
    }
  }, [initialAudienceMode]);

  const handleAudienceChange = (mode: 'solo' | 'cohort') => {
    setAudienceMode(mode);
    try {
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        const targetPath = mode === 'cohort' ? '/cohorts' : '/';
        window.history.replaceState({}, '', targetPath);
      }
    } catch {
      // Ignore in iframe
    }
  };
  const [showTaxAssumptions, setShowTaxAssumptions] = useState<boolean>(false);
  const [demoInquiryType, setDemoInquiryType] = useState<DemoInquiryType>('vip_1on1');

  // Cohort Mode Interactive Simulator States
  const [cohortSize, setCohortSize] = useState<number>(25);
  const [cohortHighlightCategory, setCohortHighlightCategory] = useState<'all' | 'institutions' | 'competitive' | 'retention' | 'security'>('all');

  const [sampleScanResult, setSampleScanResult] = useState<{
    persona: string;
    gross: number;
    taxBuffer: number;
    overhead: number;
    spendable: number;
    runwayDays: number;
    latencyMs: number;
  } | null>({
    persona: 'Independent Advisory Operator (SVB / Standard Bank Wire)',
    gross: 19450,
    taxBuffer: 4280,
    overhead: 3800,
    spendable: 11370,
    runwayDays: 89,
    latencyMs: 48
  });

  const toggleInteractiveTour = (open: boolean) => {
    setIsInteractiveTourOpen(open);
    if (onTourStateChange) onTourStateChange(open);
  };

  const handleOpenWhatIsAzuraOS = (tab?: 'pitch' | 'capabilities' | 'pilot_program' | React.MouseEvent) => {
    const targetTab = typeof tab === 'string' ? tab : 'pitch';
    setWhatIsAzuraOSTab(targetTab);
    if (onOpenWhatIsAzuraOS) {
      onOpenWhatIsAzuraOS();
    } else {
      setIsWhatIsAzuraOSModalOpen(true);
    }
  };

  const activeSettings = getBusinessSettings();
  const [simJurisdiction, setSimJurisdiction] = useState<string>(activeSettings.jurisdiction || 'US');
  const currentPreset = getJurisdictionPreset(simJurisdiction);
  const currencySym = currentPreset.currencySymbol || '$';

  // 1. FRESH, ACCURATE MARGIN HEALTH DIAGNOSTIC SIMULATOR
  // Clean initialization based on selected jurisdiction
  const initialFiguresByCurrency: Record<string, { rev: number; exp: number }> = {
    US: { rev: 15000, exp: 4200 },
    ZA: { rev: 45000, exp: 14000 },
    GB: { rev: 12000, exp: 3500 },
    EU: { rev: 14000, exp: 3900 },
    AU: { rev: 20000, exp: 5800 },
    IN: { rev: 350000, exp: 95000 }
  };

  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(() => {
    return initialFiguresByCurrency[simJurisdiction]?.rev || 15000;
  });
  const [monthlyExpense, setMonthlyExpense] = useState<number>(() => {
    return initialFiguresByCurrency[simJurisdiction]?.exp || 4200;
  });

  // Handle region switch and auto-adjust clean numbers
  const handleSelectJurisdiction = (code: string) => {
    setSimJurisdiction(code);
    const defaults = initialFiguresByCurrency[code] || { rev: 15000, exp: 4200 };
    setMonthlyRevenue(defaults.rev);
    setMonthlyExpense(defaults.exp);
    const preset = JURISDICTION_PRESETS[code] || JURISDICTION_PRESETS.US;
    if (showToast) showToast(`Region set to ${preset.name} (${preset.currencyCode}) • ${preset.vatRate}% Tax Rate`, 'info');
  };

  // Real calculations
  const calculatedMargin = useMemo(() => {
    if (monthlyRevenue <= 0) return 0;
    return Math.max(0, ((monthlyRevenue - monthlyExpense) / monthlyRevenue) * 100);
  }, [monthlyRevenue, monthlyExpense]);

  // Automated tax holding buffer calculation & explicit statutory breakdown
  const taxBreakdown = useMemo(() => {
    if (monthlyRevenue <= 0) return { vatAmount: 0, estimatedIncomeTax: 0, total: 0 };
    const vatRate = currentPreset.vatRate || 0;
    const vatAmount = vatRate > 0 
      ? Math.round(monthlyRevenue * (vatRate / (100 + vatRate)))
      : 0;
    // Estimated income / self-employment / corporate holding reserve on net margin
    const netOperating = Math.max(0, monthlyRevenue - vatAmount - monthlyExpense);
    const estimatedIncomeTax = Math.round(netOperating * (vatRate > 0 ? 0.18 : 0.22));
    return {
      vatAmount,
      estimatedIncomeTax,
      total: vatAmount + estimatedIncomeTax,
    };
  }, [monthlyRevenue, monthlyExpense, currentPreset.vatRate]);

  const calculatedTaxBuffer = taxBreakdown.total;

  // Net indicative spendable owner dividend / distribution
  const calculatedSpendableDividend = useMemo(() => {
    return Math.max(0, monthlyRevenue - monthlyExpense - calculatedTaxBuffer);
  }, [monthlyRevenue, monthlyExpense, calculatedTaxBuffer]);

  // Dynamic Strategic Margin Health Insight
  const marginHealthInsight = useMemo(() => {
    if (monthlyRevenue <= 0) {
      return 'Enter your monthly client revenue and operating overhead above to calculate your net spendable cushion.';
    }
    if (calculatedMargin < 38) {
      return `⚠️ High Cash Flow Vulnerability: At ${calculatedMargin.toFixed(1)}% operating margin, one overdue client invoice or surprise expense puts your monthly runway in immediate jeopardy. AzuraOS protects this by enforcing 50% upfront milestone contracts on all client engagements.`;
    }
    if (calculatedMargin >= 38 && calculatedMargin < 58) {
      return `⚡ Standard Operating Margin: At ${calculatedMargin.toFixed(1)}% operating margin, your monthly burn of ${currencySym}${monthlyExpense.toLocaleString()} is protected with an estimated ~45-day safety cushion. Auto-partitioning your ${currencySym}${calculatedTaxBuffer.toLocaleString()} tax buffer prevents devastating year-end statutory surprises.`;
    }
    return `🏆 Fortress Capital Efficiency: At ${calculatedMargin.toFixed(1)}% operating margin, your independent business is running at high-yield efficiency. After covering overhead and partitioning ${currencySym}${calculatedTaxBuffer.toLocaleString()} into statutory tax holding, you retain approximately ${currencySym}${calculatedSpendableDividend.toLocaleString()} in indicative safe distributable cash.`;
  }, [calculatedMargin, monthlyRevenue, monthlyExpense, calculatedTaxBuffer, calculatedSpendableDividend, currencySym]);

  const shortAuthMap: Record<string, string> = {
    ZA: 'SARS',
    US: 'IRS',
    GB: 'HMRC',
    EU: 'EU VAT',
    AU: 'ATO',
    IN: 'GST'
  };
  const shortTaxAuthority = shortAuthMap[simJurisdiction] || currentPreset.taxAuthorityLabel.split(' ')[0];

  const diagnosticPayload: MarginDiagnosticData = useMemo(() => ({
    currencySym,
    revenue: monthlyRevenue,
    expense: monthlyExpense,
    margin: calculatedMargin,
    taxBuffer: calculatedTaxBuffer,
    vatAmount: taxBreakdown.vatAmount,
    incomeTaxBuffer: taxBreakdown.estimatedIncomeTax,
    spendable: calculatedSpendableDividend,
    jurisdiction: simJurisdiction,
    jurisdictionLabel: `${currentPreset.name} • ${shortTaxAuthority}`,
    healthInsight: marginHealthInsight
  }), [currencySym, monthlyRevenue, monthlyExpense, calculatedMargin, calculatedTaxBuffer, taxBreakdown, calculatedSpendableDividend, simJurisdiction, currentPreset.name, shortTaxAuthority, marginHealthInsight]);

  // 2. INTERACTIVE RECONCILER SIMULATOR STATE
  const [reconcilerMode, setReconcilerMode] = useState<'pdf' | 'csv' | 'paste' | 'manual'>('csv');
  const [isCsvParsed, setIsCsvParsed] = useState<boolean>(true);

  const reconcilerDataByMode = {
    pdf: [
      { id: 1, date: '14 Aug', desc: 'Direct Client Wire (Advisory Retainer)', amount: 4500, type: 'credit' as const, stream: 'Consulting', status: 'PDF Flate Decoded' },
      { id: 2, date: '18 Aug', desc: 'Cloud Infrastructure & Database Ops', amount: 320, type: 'debit' as const, stream: 'Cloud SaaS', status: 'Auto-Taxed' },
      { id: 3, date: '20 Aug', desc: 'High-Speed Business Telecom & Fiber', amount: 150, type: 'debit' as const, stream: 'Utilities', status: 'Deductible' },
      { id: 4, date: '22 Aug', desc: 'Client Milestone 50% Deposit', amount: 6800, type: 'credit' as const, stream: 'Client Project', status: 'Matched' }
    ],
    csv: [
      { id: 1, date: 'Today', desc: 'Client Retainer Wire (Ref: AZ-912)', amount: 12500, type: 'credit' as const, stream: 'Consulting', status: '0.05s Parsed' },
      { id: 2, date: 'Yesterday', desc: 'Google Cloud Platform Ops', amount: 450, type: 'debit' as const, stream: 'SaaS Ops', status: 'Auto-Taxed' },
      { id: 3, date: '3 days ago', desc: 'Milestone Deposit Inflow (Strategy)', amount: 4500, type: 'credit' as const, stream: 'Advisory', status: 'Matched' },
      { id: 4, date: '4 days ago', desc: 'Figma & Notion Team Annual', amount: 320, type: 'debit' as const, stream: 'Software', status: 'Deductible' }
    ],
    paste: [
      { id: 1, date: '01 Sep', desc: 'Pasted: Enterprise Consulting Wire', amount: 9500, type: 'credit' as const, stream: 'Enterprise', status: 'Regex Matched' },
      { id: 2, date: '02 Sep', desc: 'Pasted: Cursor / GitHub Copilot', amount: 190, type: 'debit' as const, stream: 'Dev Tools', status: 'Auto-Taxed' },
      { id: 3, date: '03 Sep', desc: 'Pasted: Executive Coworking Fee', amount: 1200, type: 'debit' as const, stream: 'Office', status: 'Deductible' },
      { id: 4, date: '04 Sep', desc: 'Pasted: Client Milestone Settlement', amount: 6200, type: 'credit' as const, stream: 'Retainer', status: 'Matched' }
    ],
    manual: [
      { id: 1, date: 'Manual', desc: 'Client Retainer Inflow', amount: 5000, type: 'credit' as const, stream: 'Services', status: 'Direct Entry' },
      { id: 2, date: 'Manual', desc: 'External Tax Advisory', amount: 750, type: 'debit' as const, stream: 'Legal', status: 'Deductible' },
      { id: 3, date: 'Manual', desc: 'Business Fiber Telecom', amount: 120, type: 'debit' as const, stream: 'Utilities', status: 'Deductible' },
      { id: 4, date: 'Manual', desc: 'Workshop Ticket Settlement', amount: 3200, type: 'credit' as const, stream: 'Education', status: 'Matched' }
    ]
  };

  const handleTriggerReconcilerSim = (mode: 'pdf' | 'csv' | 'paste' | 'manual') => {
    setReconcilerMode(mode);
    setIsCsvParsed(true);
    const modeLabels = {
      pdf: '📄 Encrypted Bank PDF statement extracted in memory with zero password sharing!',
      csv: '⚡ Bank CSV parsed in 0.05 seconds directly in browser!',
      paste: '📋 Raw transaction text lines recognized & auto-categorized!',
      manual: '✍️ Direct ledger entry row created with automated tax classification!'
    };
    if (showToast) showToast(modeLabels[mode], 'success');
  };

  const handleSelectSamplePersona = (persona: 'consulting' | 'saas' | 'agency') => {
    const presets = {
      consulting: {
        persona: 'Independent Advisory Operator (SVB / Standard Bank Wire)',
        gross: 19450,
        taxBuffer: 4280,
        overhead: 3800,
        spendable: 11370,
        runwayDays: 89,
        latencyMs: 48
      },
      saas: {
        persona: 'Bootstrapped Micro-SaaS ($29/mo Stripe MRR)',
        gross: 12500,
        taxBuffer: 2650,
        overhead: 2900,
        spendable: 6950,
        runwayDays: 72,
        latencyMs: 44
      },
      agency: {
        persona: 'Boutique Design & Dev Studio (Milestone Retainers)',
        gross: 27800,
        taxBuffer: 6100,
        overhead: 7400,
        spendable: 14300,
        runwayDays: 115,
        latencyMs: 52
      }
    };
    setSampleScanResult(presets[persona]);
    setIsCsvParsed(true);
    if (showToast) showToast(`⚡ Parsed ${presets[persona].persona} statement in ${presets[persona].latencyMs}ms with zero password sharing!`, 'success');
  };

  const handleLaunchTrial = (prefilledEmail?: string) => {
    if (onOpenAuthModal) {
      onOpenAuthModal(prefilledEmail);
    } else {
      onGetStarted();
    }
  };

  return (
    <div className="space-y-12 sm:space-y-16 animate-fade-in relative max-w-5xl mx-auto px-4 sm:px-6">
      <SEO 
        title={audienceMode === 'solo' 
          ? 'AzuraOS — Autonomous Chief of Staff for the 1-Person Unicorn'
          : 'Cohort Runway Radar & Solvency Pilot | AzuraOS'
        }
        description={audienceMode === 'solo'
          ? 'The Chief of Staff that\'s always by your side. Orchestrate strategy, client agreements, pitch decks, and daily operations with an autonomous CFO & Treasury suite.'
          : 'Turnkey 30-day financial governance and batch runway pilot for accelerators and incubators with Multi-Provider BYOK.'
        }
        path={audienceMode === 'solo' ? '/' : '/cohorts'}
      />
      
      {/* 🚀 1. HIGH-CONVERSION HERO: AUTONOMOUS CHIEF OF STAFF FOR THE 1-PERSON UNICORN */}
      <section className="text-center pt-6 sm:pt-10 pb-4 space-y-5 sm:space-y-6 relative">
        
        {/* 🔀 GTM AUDIENCE BIFURCATION TOGGLE */}
        <div className="inline-flex p-1 bg-[#0E1214] border border-[#232B2E] rounded-2xl shadow-inner max-w-md mx-auto">
          <button
            type="button"
            onClick={() => handleAudienceChange('solo')}
            className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer flex items-center gap-1.5 ${
              audienceMode === 'solo'
                ? 'bg-[#14B8A6] text-black shadow-md'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Sparkles size={13} />
            <span>Chief of Staff for Solo Founders</span>
          </button>
          <button
            type="button"
            onClick={() => handleAudienceChange('cohort')}
            className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer flex items-center gap-1.5 ${
              audienceMode === 'cohort'
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Building2 size={13} />
            <span>For Accelerators &amp; Cohorts</span>
          </button>
        </div>

        {/* Eyebrow Tag */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#14B8A6]/10 text-[#14B8A6] rounded-full border border-[#14B8A6]/25 text-xs font-bold tracking-wider uppercase shadow-xs">
          <Sparkles size={13} className="text-[#14B8A6]" /> 
          <span>
            {audienceMode === 'solo' 
              ? 'Autonomous Chief of Staff • 1-Person Unicorn Operating System • Always By Your Side'
              : 'Institutional Cohort Solvency Radar • Accelerator & Incubator Governance'}
          </span>
        </div>
        
        {/* Main Headline */}
        <div className="space-y-3.5 max-w-4xl mx-auto">
          {audienceMode === 'solo' ? (
            <>
              <h1 className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-white tracking-tight leading-[1.12]">
                Stop Drowning in Admin Chaos. <br className="hidden sm:inline" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#14B8A6] via-teal-300 to-emerald-400">
                  Meet Your Autonomous Chief of Staff.
                </span>
              </h1>
              <p className="text-sm sm:text-lg text-gray-300 font-normal max-w-3xl mx-auto leading-relaxed">
                Designed for $10k–$50k/mo solo founders, consultants, and micro-agencies who refuse to burn 20 hours a week on admin. AzuraOS acts as your non-sycophantic Chief of Staff—orchestrating strategy, client agreements, and pitch decks—with an autonomous CFO &amp; Treasury suite protecting your margins and taxes quietly in the background.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-white tracking-tight leading-[1.12]">
                Your Cohort Teaches Founders to Build. <br className="hidden sm:inline" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-300 to-emerald-300">
                  What Helps Them Run the Business?
                </span>
              </h1>
              <p className="text-sm sm:text-lg text-gray-300 font-normal max-w-3xl mx-auto leading-relaxed">
                A founder can build a product, pitch investors and reach Demo Day—yet still struggle to manage cash flow, upcoming tax obligations, client agreements, and everyday administrative overhead.
              </p>
              <p className="text-xs sm:text-sm text-teal-300/90 font-mono max-w-2xl mx-auto">
                AzuraOS gives accelerators and incubators a structured operational system to extend practical business-operating support beyond mentorship.
              </p>
            </>
          )}
        </div>

        {/* Primary Call-to-Action Bar */}
        <div className="pt-2 max-w-3xl mx-auto space-y-3">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {audienceMode === 'solo' ? (
              <button
                onClick={() => {
                  if (onOpenQuickStart) {
                    onOpenQuickStart();
                  } else {
                    setIsQuickStartOpen(true);
                  }
                }}
                className="w-full sm:w-auto flex-1 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-black rounded-xl h-12 px-6 flex items-center justify-center gap-2 shadow-lg shadow-[#14B8A6]/25 active:scale-95 transition-all cursor-pointer group text-sm"
              >
                <Zap size={16} className="text-black fill-black" />
                <span>Deploy Chief of Staff (Free Trial)</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setDemoInquiryType('accelerator_pilot');
                  setIsRequestDemoOpen(true);
                }}
                className="w-full sm:w-auto flex-1 bg-gradient-to-r from-purple-500 via-teal-400 to-emerald-400 hover:from-purple-400 hover:to-emerald-300 text-black font-black rounded-xl h-12 px-6 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 active:scale-95 transition-all cursor-pointer group text-sm"
              >
                <Building2 size={16} className="text-black" />
                <span>Request Cohort Pilot Briefing</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform text-black" />
              </button>
            )}

            {audienceMode === 'cohort' ? (
              <button
                type="button"
                onClick={() => handleOpenWhatIsAzuraOS('pilot_program')}
                className="w-full sm:w-auto bg-[#151819] hover:bg-[#1F2426] border border-purple-500/40 hover:border-purple-400 text-purple-300 hover:text-white font-bold rounded-xl h-12 px-5 flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm active:scale-95 text-xs"
              >
                <FileText size={14} className="text-purple-400" />
                <span>Pilot Specification &amp; RFP</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleOpenWhatIsAzuraOS('capabilities')}
                className="w-full sm:w-auto bg-[#151819] hover:bg-[#1F2426] border border-teal-500/40 hover:border-teal-400 text-teal-300 hover:text-white font-bold rounded-xl h-12 px-5 flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm active:scale-95 text-xs"
              >
                <Sliders size={14} className="text-[#14B8A6]" />
                <span>Chief of Staff vs CFO Suite</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                if (audienceMode === 'cohort') {
                  if (onOpenCohortPortal) {
                    onOpenCohortPortal();
                  } else {
                    handleLaunchTrial();
                  }
                } else {
                  handleLaunchTrial();
                }
              }}
              className="w-full sm:w-auto bg-[#151819] hover:bg-[#1F2426] border border-[#2A2F31] hover:border-gray-500 text-gray-300 hover:text-white font-bold rounded-xl h-12 px-4 flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm active:scale-95 text-xs"
            >
              {audienceMode === 'cohort' ? (
                <>
                  <Award size={14} className="text-teal-400" />
                  <span>Redeem Founder Voucher</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} className="text-gray-400" />
                  <span>Start 7-Day Trial</span>
                </>
              )}
            </button>
          </div>

          {/* 🌟 FOUNDER CRISIS PROMPT DECK: INTERACTIVE LIVE HERO SIMULATOR */}
          <div className="pt-2">
            <FounderCrisisPromptDeck
              onLaunchTrial={handleLaunchTrial}
              onOpenQuickStart={onOpenQuickStart || (() => setIsQuickStartOpen(true))}
              onOpenReconciler={onOpenReconciler}
              onLoadDemo={onLoadDemo}
              showToast={showToast}
            />
          </div>

          {/* 4 Executive Pillars of the 1-Person Unicorn */}
          {audienceMode === 'solo' && (
            <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-2.5 text-left">
              <div className="p-3 bg-[#111415] border border-[#21272A] rounded-xl space-y-1 group hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-1.5 text-teal-400 text-xs font-bold font-mono">
                  <Brain size={13} />
                  <span>01. Strategy &amp; Ops</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Non-sycophantic debriefs, operational priorities &amp; voice directives.
                </p>
              </div>

              <div className="p-3 bg-[#111415] border border-[#21272A] rounded-xl space-y-1 group hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-1.5 text-blue-400 text-xs font-bold font-mono">
                  <Scale size={13} />
                  <span>02. Legal &amp; Deals</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Instant client agreement proformas, scope defense &amp; kill-fee clauses.
                </p>
              </div>

              <div className="p-3 bg-[#111415] border border-[#21272A] rounded-xl space-y-1 group hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-1.5 text-purple-400 text-xs font-bold font-mono">
                  <BarChart3 size={13} />
                  <span>03. Pitch &amp; Capital</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Institutional 10-slide decks, investor proposals &amp; runway milestones.
                </p>
              </div>

              <div className="p-3 bg-[#111415] border border-[#21272A] rounded-xl space-y-1 group hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold font-mono">
                  <DollarSign size={13} />
                  <span>04. CFO &amp; Treasury</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Autonomous tax buffers, 0.05s bank statement parsing &amp; live margins.
                </p>
              </div>
            </div>
          )}

          {/* Dedicated Sub-link for Cohort founders visiting landing page */}
          {audienceMode === 'cohort' && (
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => {
                  if (onOpenCohortPortal) onOpenCohortPortal();
                }}
                className="text-xs text-gray-400 hover:text-teal-300 transition-colors inline-flex items-center gap-1.5 cursor-pointer group font-mono"
              >
                <span>Accepted into an active cohort?</span>
                <span className="text-teal-400 underline underline-offset-4 group-hover:text-teal-300">
                  Activate your sponsored seat with your voucher code →
                </span>
              </button>
            </div>
          )}

          {/* Micro Trust Proof Bar */}
          <div className="p-3 bg-[#0D1011] border border-[#1E2629] rounded-xl flex flex-wrap items-center justify-center gap-x-3 sm:gap-x-5 gap-y-1.5 text-[11px] text-gray-300 font-mono">
            <span className="flex items-center gap-1.5 text-teal-400 font-bold">
              <Globe size={13} className="text-teal-400" /> Global: US • UK • EU • ZA • AU • IN
            </span>
            <span className="text-gray-700 hidden sm:inline">•</span>
            <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
              <Zap size={13} className="text-emerald-400" /> 0.05s Bank Statement Parser
            </span>
            <span className="text-gray-700 hidden sm:inline">•</span>
            <span className="flex items-center gap-1.5 text-cyan-300 font-bold">
              <ShieldCheck size={13} className="text-cyan-400" /> Air-Gapped Zero-Password Vault
            </span>
            <span className="text-gray-700 hidden sm:inline">•</span>
            <span className="flex items-center gap-1.5 text-purple-300 font-bold">
              <Bot size={13} className="text-purple-400" /> Zero AI Model Training
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-gray-400 font-mono pt-1">
            <span className="flex items-center gap-1.5 text-teal-400 font-semibold">
              <Check size={13} /> {audienceMode === 'cohort' ? 'Turnkey 30-Day Pilot Ready' : '7-Day Unrestricted Access'} • 6 Global Currencies Supported
            </span>
            <span className="text-gray-700 hidden sm:inline">•</span>
            <button
              type="button"
              onClick={() => toggleInteractiveTour(true)}
              className="text-amber-400 hover:text-white underline cursor-pointer"
            >
              Interactive 60s Tour ↓
            </button>
          </div>
        </div>
      </section>

      {/* 🎯 2. AUDIENCE-SPECIFIC DEEP WORKSPACE SECTIONS */}
      {audienceMode === 'cohort' ? (
        /* ========================================================================= */
        /* 🏛️ INSTITUTIONAL COHORT & ACCELERATOR ARCHITECTURE SUITE                  */
        /* ========================================================================= */
        <div className="space-y-12 sm:space-y-16 animate-fade-in">
          
          {/* SECTION C0: THE OPERATIONAL GAP & THE COHORT SUPPORT INTERVENTION */}
          <section className="space-y-8 text-left">
            <div className="text-center space-y-3 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-purple-500/10 text-purple-300 rounded-full border border-purple-500/25 text-[11px] font-mono font-bold uppercase tracking-wider">
                <Target size={13} className="text-purple-400" />
                <span>The Post-Demo Day Reality • Operational Intervention</span>
              </div>
              <h2 className="font-display font-black text-2xl sm:text-4xl text-white tracking-tight leading-tight">
                The Gap Between Building a Product and Running a Company
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">
                Accelerators excel at teaching founders how to build, iterate, and pitch. But post-programme mortality rarely happens because of product architecture—it happens when founders hit the operational and financial administration wall.
              </p>
            </div>

            {/* Side-by-Side Reality Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              
              {/* Card 1: What Accelerators Teach */}
              <div className="p-6 rounded-3xl bg-[#121516] border border-[#232B2E] space-y-4">
                <div className="flex items-center gap-3 border-b border-[#1F2426] pb-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 flex items-center justify-center shrink-0">
                    <Award size={18} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm">Where Accelerators Excel</h3>
                    <span className="text-[10px] font-mono text-gray-400 uppercase">The Building &amp; Pitching Phase</span>
                  </div>
                </div>

                <ul className="space-y-2.5 text-xs text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Customer Discovery &amp; Validation:</strong> Refining value propositions and finding product-market fit.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Pitch Deck Polish &amp; Storytelling:</strong> Crafting the narrative for seed and angel investment.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Demo Day Presentation:</strong> High-energy showcase to venture capitalists and partners.</span>
                  </li>
                </ul>

                <div className="p-3 bg-[#0C0E0F] border border-[#1F2426] rounded-xl text-[11px] text-gray-400 font-mono">
                  ✨ Outcome: A viable prototype and an investor deck.
                </div>
              </div>

              {/* Card 2: Where Founders Struggle Post-Demo Day */}
              <div className="p-6 rounded-3xl bg-gradient-to-br from-[#161318] via-[#141215] to-[#121516] border border-rose-500/30 space-y-4">
                <div className="flex items-center gap-3 border-b border-rose-500/20 pb-3">
                  <div className="w-9 h-9 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 flex items-center justify-center shrink-0">
                    <AlertCircle size={18} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm">Where Founders Struggle</h3>
                    <span className="text-[10px] font-mono text-rose-300 uppercase">The Post-Demo Day Reality</span>
                  </div>
                </div>

                <ul className="space-y-2.5 text-xs text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-rose-400 font-bold shrink-0">✕</span>
                    <span><strong>Trapped Receivables:</strong> Cash earned on paper is stuck in uncollected client invoices for 45+ days.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-400 font-bold shrink-0">✕</span>
                    <span><strong>The False Runway Shock:</strong> Co-mingling statutory taxes with operating cash, creating sudden tax debt.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-400 font-bold shrink-0">✕</span>
                    <span><strong>Administrative Fatigue:</strong> Burning 15+ hours a week fighting disconnected accounting tools and spreadsheets.</span>
                  </li>
                </ul>

                <div className="p-3 bg-rose-950/20 border border-rose-500/30 rounded-xl text-[11px] text-rose-300 font-mono">
                  ⚠️ Danger: 82% of early-stage failures stem from cash flow mismanagement.
                </div>
              </div>

            </div>

            {/* The 4 Testable Pilot Outcomes Strip */}
            <div className="p-6 sm:p-8 rounded-3xl bg-[#121617] border border-[#232B2E] space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2426] pb-4">
                <div>
                  <span className="text-[10px] font-mono text-teal-400 font-bold uppercase tracking-wider">The Pilot Framework</span>
                  <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                    Start with a Defined Pilot. Measure Practical Outcomes. Decide What Deserves to Scale.
                  </h3>
                </div>
                <span className="text-xs text-gray-400 font-mono bg-[#181C1E] px-3 py-1 rounded-full border border-[#2A3438] shrink-0">
                  30–60 Day Cohort Test
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                
                <div className="p-4 rounded-2xl bg-[#15191A] border border-[#252D30] space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-300 flex items-center justify-center font-bold">
                    ⏱️
                  </div>
                  <strong className="text-white text-sm block">Reclaim Admin Hours</strong>
                  <p className="text-gray-400 text-[11px] leading-relaxed">
                    Founders spend significantly less time managing disconnected invoices, spreadsheets, and manual calculations.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-[#15191A] border border-[#252D30] space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold">
                    📊
                  </div>
                  <strong className="text-white text-sm block">Forward Visibility</strong>
                  <p className="text-gray-400 text-[11px] leading-relaxed">
                    Continuous clarity on True Safe Spendable Draw, automated statutory tax buffers, and genuine cash runway days.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-[#15191A] border border-[#252D30] space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 flex items-center justify-center font-bold">
                    💡
                  </div>
                  <strong className="text-white text-sm block">Informed Decisions</strong>
                  <p className="text-gray-400 text-[11px] leading-relaxed">
                    Standardized 50% milestone deposits and automated margin guardrails prevent premature burn and risky hiring.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-[#15191A] border border-[#252D30] space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 flex items-center justify-center font-bold">
                    🛡️
                  </div>
                  <strong className="text-white text-sm block">Habits Beyond Demo Day</strong>
                  <p className="text-gray-400 text-[11px] leading-relaxed">
                    Founders build rigorous financial and contractual operating habits they continue using long after the programme ends.
                  </p>
                </div>

              </div>

              {/* Concrete Workflow Breakdown: What Founders Actually Do */}
              <div className="p-5 rounded-2xl bg-[#0E1112] border border-teal-500/20 space-y-3">
                <div className="text-xs font-bold text-teal-300 uppercase tracking-wider flex items-center gap-2">
                  <Sparkles size={14} />
                  <span>What Participating Founders Actually Do in AzuraOS</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                  <div className="flex items-start gap-2">
                    <span className="text-teal-400 font-bold">1.</span>
                    <span><strong>30s Milestone Billing:</strong> Issue professional VAT/tax invoices, proformas, and kill-fee agreements directly to clients.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-400 font-bold">2.</span>
                    <span><strong>Forward Cash-Flow Radar:</strong> Automatically isolate tax reserves (IRS, HMRC, SARS, EU) before calculating spendable draw.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-400 font-bold">3.</span>
                    <span><strong>Multi-Provider AI BYOK:</strong> Bring their own API keys (Gemini, OpenAI, Claude, Groq) with zero data leakage or platform retention.</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          {/* SECTION C1: DYNAMIC COHORT ROI & SOLVENCY IMPACT CALCULATOR */}
          <section id="cohort-impact-calculator" className="scroll-mt-6 bg-gradient-to-br from-[#121618] via-[#151819] to-[#0E1517] border-2 border-purple-500/30 rounded-3xl p-4 sm:p-8 space-y-6 shadow-2xl text-left overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#2A2F31] pb-4 sm:pb-5">
              <div className="space-y-1 min-w-0 flex-1">
                <div className="flex items-start sm:items-center gap-2.5">
                  <span className="p-2 rounded-xl bg-purple-500/10 text-purple-300 border border-purple-500/20 shrink-0 mt-0.5 sm:mt-0">
                    <Calculator size={18} className="text-purple-400" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display font-extrabold text-lg sm:text-2xl text-white tracking-tight leading-tight">
                      Institutional Pilot Cohort Impact Calculator
                    </h2>
                    <span className="text-[10px] sm:text-[11px] font-mono text-purple-300 uppercase tracking-wider font-semibold block mt-0.5">
                      Real-Time Batch Health &amp; Solvency Projection
                    </span>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light pt-1">
                  Select your cohort size to project aggregate operational hours saved, statutory tax buffers ring-fenced, and portfolio survival rates.
                </p>
              </div>

              {/* Cohort Size Selector */}
              <div className="grid grid-cols-4 sm:flex items-center gap-1.5 bg-[#0C0E0F] p-1.5 rounded-2xl border border-[#2A2F31] w-full sm:w-auto shrink-0">
                {[10, 25, 50, 100].map(size => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setCohortSize(size)}
                    className={`px-2.5 sm:px-3.5 py-2 rounded-xl text-xs font-mono font-bold transition-all text-center cursor-pointer ${
                      cohortSize === size
                        ? 'bg-purple-600 text-white font-extrabold shadow-md'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {size} Seats
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamically Calculated Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-4">
              <div className="p-4 sm:p-5 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1.5">
                <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider block font-semibold">
                  Admin Hours Saved / Month
                </span>
                <div className="text-2xl sm:text-3xl font-black text-white font-mono flex items-center gap-1">
                  <span className="text-[#14B8A6]">~{(cohortSize * 16).toLocaleString()}</span>
                  <span className="text-xs text-gray-400 font-sans font-normal">hrs/mo</span>
                </div>
                <p className="text-xs text-gray-400 leading-tight">
                  Saved on manual invoice design, spreadsheet chasing, and calendar scheduling across {cohortSize} founders.
                </p>
              </div>

              <div className="p-4 sm:p-5 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1.5">
                <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider block font-semibold">
                  Projected Viability Rate
                </span>
                <div className="text-2xl sm:text-3xl font-black text-emerald-400 font-mono flex items-center gap-1">
                  <span>92%</span>
                  <span className="text-xs text-gray-400 font-sans font-normal">({Math.round(cohortSize * 0.92)}/{cohortSize} active)</span>
                </div>
                <p className="text-xs text-gray-400 leading-tight">
                  Enforcing 50% milestone deposits and ring-fencing taxes before cash is spent as false runway.
                </p>
              </div>

              <div className="p-4 sm:p-5 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1.5">
                <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider block font-semibold">
                  Audit Readiness
                </span>
                <div className="text-2xl sm:text-3xl font-black text-purple-300 font-mono">
                  100%
                </div>
                <p className="text-xs text-gray-400 leading-tight">
                  Structured balance ledger exports (JSON/CSV) ready for CPAs, lenders, and grant reporting.
                </p>
              </div>
            </div>

            {/* Batch Aggregates Summary Strip */}
            <div className="p-3.5 sm:p-4 bg-[#0B0E0F] border border-[#1F2426] rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 text-center font-mono text-xs">
              <div className="space-y-0.5">
                <span className="text-[10px] text-gray-400 block">Batch Gross Capacity</span>
                <strong className="text-white text-xs sm:text-sm">~${(cohortSize * 45000).toLocaleString()}</strong>
              </div>
              <div className="space-y-0.5">
                <span className="text-[10px] text-amber-300 block">Statutory Tax Buffer</span>
                <strong className="text-amber-400 text-xs sm:text-sm">~${(cohortSize * 4280).toLocaleString()}</strong>
              </div>
              <div className="space-y-0.5">
                <span className="text-[10px] text-teal-300 block">Avg Runway Added</span>
                <strong className="text-teal-400 text-xs sm:text-sm">+42 Days</strong>
              </div>
              <div className="space-y-0.5">
                <span className="text-[10px] text-purple-300 block">Currencies Supported</span>
                <strong className="text-purple-300 text-xs sm:text-sm">6 Global</strong>
              </div>
            </div>

            {/* Direct Action for Calculated Cohort */}
            <div className="p-4 sm:p-5 rounded-2xl bg-[#0D1011] border border-purple-500/30 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-gray-300 text-center sm:text-left space-y-0.5">
                <strong className="text-white text-sm block">Ready to deploy for a {cohortSize}-founder cohort?</strong>
                <span>Includes turnkey onboarding playbook, multi-jurisdiction tax engine, and dedicated sponsor audit desk.</span>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => handleOpenWhatIsAzuraOS('pilot_program')}
                  className="flex-1 sm:flex-initial px-4 py-2.5 bg-[#1F2426] hover:bg-[#2A2F31] text-purple-300 hover:text-white font-bold text-xs rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <FileText size={13} />
                  <span>Full Briefing (Tab 3)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDemoInquiryType('accelerator_pilot');
                    setIsRequestDemoOpen(true);
                  }}
                  className="flex-1 sm:flex-initial px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs rounded-xl transition shadow-lg shadow-purple-600/30 flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-95"
                >
                  <span>Reserve {cohortSize}-Seat Pilot</span>
                  <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </section>

          {/* SECTION C2: KEY COMPETITIVE ADVANTAGES & PILOT READINESS */}
          <section className="space-y-5 text-left">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-mono font-bold text-purple-400 uppercase tracking-wider">
                  Pilot Specification
                </span>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl text-white tracking-tight flex items-center gap-2">
                  <Sparkles size={20} className="text-purple-400" />
                  <span>Key Competitive Advantages &amp; Pilot Readiness</span>
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Why institutional sponsors, incubator leads, and enterprise accelerators choose AzuraOS.
                </p>
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 custom-scrollbar">
                {[
                  { id: 'all', label: 'All Advantages' },
                  { id: 'institutions', label: 'Institutional Ready' },
                  { id: 'retention', label: 'Runway Defense' },
                  { id: 'competitive', label: 'Zero Bloat' },
                  { id: 'security', label: 'Data Sovereignty' }
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setCohortHighlightCategory(f.id as any)}
                    className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all whitespace-nowrap cursor-pointer ${
                      cohortHighlightCategory === f.id
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-[#151819] text-gray-400 hover:text-white border border-[#2A2F31]'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4">
              {(cohortHighlightCategory === 'all' 
                ? PILOT_HIGHLIGHTS 
                : PILOT_HIGHLIGHTS.filter(h => h.category === cohortHighlightCategory)
              ).map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.id}
                    className="p-4 sm:p-5 bg-[#151819] border border-[#2A2F31] hover:border-purple-500/50 rounded-2xl space-y-3 transition-all group shadow-md"
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 flex items-center justify-center shrink-0">
                            <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs sm:text-sm font-bold text-white group-hover:text-purple-300 transition-colors leading-snug">
                              {item.title}
                            </h4>
                            <span className="text-[10px] font-mono text-gray-400 block mt-0.5">
                              {item.badge}
                            </span>
                          </div>
                        </div>
                        <span className="hidden sm:inline-flex px-2.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-[10px] font-mono font-extrabold border border-purple-500/30 whitespace-nowrap shrink-0">
                          {item.metric}
                        </span>
                      </div>
                      
                      {/* Mobile metric pill */}
                      <div className="sm:hidden flex items-center justify-end">
                        <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-[9px] font-mono font-extrabold border border-purple-500/30 whitespace-nowrap">
                          {item.metric}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-gray-300 leading-relaxed font-light">
                      {item.description}
                    </p>

                    <div className="p-3 rounded-xl bg-[#0F1213] border border-[#1F2426] text-[11px] text-teal-300/90 leading-snug flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-[#14B8A6] shrink-0 mt-0.5" />
                      <span><strong>Pilot Value:</strong> {item.pilotBenefit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* SECTION C3: 30-DAY INSTITUTIONAL PILOT ROLLOUT BLUEPRINT */}
          <section className="bg-[#121516] border border-[#2A2F31] rounded-3xl p-6 sm:p-8 space-y-5 text-left shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[11px] font-mono font-bold text-teal-400 uppercase tracking-wider">
                  Implementation Playbook
                </span>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl text-white tracking-tight flex items-center gap-2">
                  <Clock size={20} className="text-teal-400" />
                  <span>The 30-Day Institutional Pilot Rollout Blueprint</span>
                </h3>
              </div>
              <span className="text-[10px] font-mono text-gray-400 uppercase bg-[#1A2022] px-2.5 py-1 rounded-lg border border-[#2A3438] hidden sm:inline">
                Frictionless Deployment
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="flex flex-col p-4 bg-[#151819] rounded-2xl border border-[#1F2426] space-y-2">
                <div className="flex items-center gap-2 text-teal-400 font-mono font-bold text-xs">
                  <span className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold">1</span>
                  <span>Week 1 — Provisioning</span>
                </div>
                <strong className="text-white text-sm">Turnkey Cohort Onboarding</strong>
                <p className="text-gray-400 leading-relaxed font-light text-[11px]">
                  Bulk account setup for cohort founders. Instant automated configuration of local tax presets, currency formatting, and branding.
                </p>
              </div>

              <div className="flex flex-col p-4 bg-[#151819] rounded-2xl border border-[#1F2426] space-y-2">
                <div className="flex items-center gap-2 text-purple-400 font-mono font-bold text-xs">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center font-bold">2</span>
                  <span>Weeks 2–3 — Operations</span>
                </div>
                <strong className="text-white text-sm">Live Milestone Invoicing</strong>
                <p className="text-gray-400 leading-relaxed font-light text-[11px]">
                  Founders issue live client invoices with 50% deposit milestones, self-manage booking slots, and track real-time margin defense.
                </p>
              </div>

              <div className="flex flex-col p-4 bg-[#151819] rounded-2xl border border-[#1F2426] space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 font-mono font-bold text-xs">
                  <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">3</span>
                  <span>Week 4 — Reporting</span>
                </div>
                <strong className="text-white text-sm">Health Summary &amp; Audit</strong>
                <p className="text-gray-400 leading-relaxed font-light text-[11px]">
                  Generate comprehensive batch financial viability scorecards, tax readiness audit logs, and sponsor ROI reports.
                </p>
              </div>
            </div>
          </section>

          {/* SECTION C4: OFFICIAL PROCUREMENT PACK & COHORT RFP */}
          <section className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#151819] via-[#12181A] to-[#121E20] border-2 border-purple-500/40 flex flex-col sm:flex-row items-center justify-between gap-5 text-left shadow-2xl">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Landmark size={18} className="text-purple-400" />
                <h3 className="text-base sm:text-lg font-bold text-white">
                  Official Procurement Pack &amp; Pilot RFP
                </h3>
              </div>
              <p className="text-xs text-gray-300 max-w-xl leading-relaxed font-light">
                Request our complete enterprise specification memo, data sovereignty audit pack, cohort volume pricing sheet, and legal terms for investment committees.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setDemoInquiryType('accelerator_pilot');
                  setIsRequestDemoOpen(true);
                }}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap active:scale-95"
              >
                <Landmark size={14} />
                <span>Request Cohort Pilot Proposal</span>
              </button>
            </div>
          </section>

        </div>
      ) : (
        /* ========================================================================= */
        /* 💼 SOLO OPERATOR & STARTUP WORKSPACE SUITE                                 */
        /* ========================================================================= */
        <>
          {/* 🛑 1. THE PAIN POINT: SOLOPRENEUR OVERWHELM & CONTEXT SWITCHING */}
          <section className="space-y-8 text-left">
            <div className="text-center space-y-3 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-rose-500/10 text-rose-300 rounded-full border border-rose-500/25 text-[11px] font-mono font-bold uppercase tracking-wider">
                <AlertCircle size={13} className="text-rose-400" />
                <span>The $10k–$50k/Month Solo Bottleneck</span>
              </div>
              <h2 className="font-display font-black text-2xl sm:text-4xl text-white tracking-tight leading-tight">
                You Built a Real Business. Why Does It Feel Like You're Doing 5 Jobs?
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">
                When you make $15k to $50k a month alone, your biggest enemy isn't competition—it's context switching. Juggling client contracts, slide decks, operational triage, and bookkeeping burns the high-leverage hours that actually make money.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl bg-[#121516] border border-rose-500/25 space-y-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-sm">
                  01
                </div>
                <h4 className="font-bold text-white text-sm">Administrative Exhaustion</h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Drafting SOWs from scratch, formatting client emails, and building investor decks one by one eats 15+ hours every week.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#121516] border border-rose-500/25 space-y-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-sm">
                  02
                </div>
                <h4 className="font-bold text-white text-sm">The "Yes-Man" AI Trap</h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Generic AI chatbots just flatter you and hallucinate answers. Solo founders need rigorous, non-sycophantic stress-tests to stay solvent.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#121516] border border-rose-500/25 space-y-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold text-sm">
                  03
                </div>
                <h4 className="font-bold text-white text-sm">Spreadsheet Anxiety</h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Traditional accounting tools are clunky, backwards-looking, and tedious. You end up guessing your true take-home pay and tax debt.
                </p>
              </div>
            </div>
          </section>

          {/* 👑 2. THE CORE SOLUTION: TWO DISTINCT ENGINES IN ONE OS */}
          <section className="space-y-6 text-left">
            <div className="text-center space-y-2 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-teal-500/10 text-teal-300 rounded-full border border-teal-500/25 text-[11px] font-mono font-bold uppercase tracking-wider">
                <Sparkles size={13} className="text-[#14B8A6]" />
                <span>The Architecture • Executive Leverage</span>
              </div>
              <h2 className="font-display font-black text-2xl sm:text-4xl text-white tracking-tight leading-tight">
                Two Purpose-Built Engines. Zero Bloat.
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">
                AzuraOS puts the Autonomous Chief of Staff at the center of your daily operations, with an autonomous CFO &amp; Treasury suite working quietly in the background.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Card 1: Core Engine */}
              <div className="p-6 rounded-3xl bg-[#131718] border-2 border-teal-500/40 space-y-4 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-[#212A2D] pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center font-bold">
                      <Sparkles size={18} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">Chief of Staff Command Center</h3>
                      <span className="text-[10px] font-mono text-teal-400 font-bold uppercase">The Primary Executive Engine</span>
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300 text-[10px] font-mono font-bold">CORE</span>
                </div>
                <ul className="space-y-2.5 text-xs text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-teal-400 shrink-0 mt-0.5" />
                    <span><strong>Non-Sycophantic Strategy:</strong> Direct, unconstrained challenge sessions to stress-test pricing, positioning, and kill distractions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-teal-400 shrink-0 mt-0.5" />
                    <span><strong>Client Agreement Proformas:</strong> Generate protective SOWs with 50% milestone deposits and kill-fee defense in 30 seconds.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-teal-400 shrink-0 mt-0.5" />
                    <span><strong>Pitch &amp; Capital Architect:</strong> Build institutional 10-slide YC/Techstars pitch decks directly from your venture metrics.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-teal-400 shrink-0 mt-0.5" />
                    <span><strong>Voice Directives:</strong> Turn raw voice notes into structured client scopes and meeting debriefs with native Gemini audio.</span>
                  </li>
                </ul>
              </div>

              {/* Card 2: Subsidiary Engine */}
              <div className="p-6 rounded-3xl bg-[#121516] border border-emerald-500/30 space-y-4 shadow-xl relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-[#1F2426] pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold">
                      <DollarSign size={18} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">CFO &amp; Treasury Suite</h3>
                      <span className="text-[10px] font-mono text-emerald-400 font-bold uppercase">The Subsidiary Financial Engine</span>
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-mono font-bold">AUTOMATED</span>
                </div>
                <ul className="space-y-2.5 text-xs text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Automated Tax Ring-Fencing:</strong> Separates 25%–30% statutory buffers into safe reserves automatically on every payment.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>0.05s Air-Gapped Bank Reconciler:</strong> Ingest CSV bank statements in 50ms with zero password sharing or Plaid tracking.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Real-Time Margin Defense:</strong> Immediate audio/visual feedback whenever new bookings or expenses shift your net profit margin.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Global Invoicing:</strong> Clean multi-currency invoicing with US, UK, EU, ZA, AU &amp; IN VAT/Tax compliance built-in.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* 💰 3. SUBSIDIARY ENGINE IN ACTION: LIVE CFO DIAGNOSTIC */}
          <section id="margin-diagnostic-calculator" className="scroll-mt-6 space-y-6 text-left pt-6 border-t border-[#1F2426]">
            <div className="text-center space-y-2 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-emerald-500/10 text-emerald-300 rounded-full border border-emerald-500/25 text-[11px] font-mono font-bold uppercase tracking-wider">
                <Calculator size={13} className="text-emerald-400" />
                <span>Subsidiary Engine in Action • CFO &amp; Treasury Live Diagnostic</span>
              </div>
              <h2 className="font-display font-extrabold text-2xl sm:text-4xl text-white tracking-tight leading-tight">
                Calculate Your Safe Spendable Draw
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">
                See how AzuraOS automatically separates what you can safely draw as profit from what belongs to the tax office. Instant clarity for US, UK, South Africa, Europe, Australia, and India.
              </p>

              {/* Region / Currency Selector */}
              <div className="flex flex-wrap items-center justify-center gap-1.5 pt-2">
                {(['US', 'ZA', 'GB', 'EU', 'AU', 'IN'] as const).map((code) => {
                  const preset = JURISDICTION_PRESETS[code];
                  const isActive = simJurisdiction === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleSelectJurisdiction(code)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer ${
                        isActive
                          ? 'bg-[#14B8A6] text-black shadow-md font-extrabold'
                          : 'bg-[#121516] text-gray-400 border border-[#232B2E] hover:text-white hover:border-gray-500'
                      }`}
                    >
                      <span>{preset.flag}</span>
                      <span>{preset.currencyCode} ({preset.currencySymbol})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* The Diagnostic Engine Card */}
            <div className="bg-[#121517] border border-[#14B8A6]/40 rounded-3xl p-5 sm:p-8 shadow-2xl space-y-6">
              
              {/* Inputs Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                
                {/* Input 1: Monthly Revenue / Inflow */}
                <div className="p-4 bg-[#0A0D0E] border border-[#20282B] rounded-2xl space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <label htmlFor="calc-monthly-revenue" className="font-semibold text-gray-200 flex items-center gap-1.5 cursor-pointer">
                      <TrendingUp size={14} className="text-emerald-400" />
                      <span>Estimated Monthly Billings / Inflows</span>
                    </label>
                    <span className="text-[11px] font-mono text-teal-400 font-bold">
                      {currencySym}{monthlyRevenue.toLocaleString()}
                    </span>
                  </div>

                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-mono font-bold text-gray-300 text-sm">
                      {currencySym}
                    </span>
                    <input
                      id="calc-monthly-revenue"
                      name="calcMonthlyRevenue"
                      aria-label="Estimated Monthly Billings or Inflows"
                      type="number"
                      min="0"
                      step="500"
                      value={monthlyRevenue}
                      onChange={(e) => setMonthlyRevenue(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full bg-[#14191B] border border-[#2A3438] focus:border-[#14B8A6] rounded-xl pl-9 pr-4 py-2.5 text-sm font-mono font-bold text-white outline-none transition"
                    />
                  </div>

                  {/* Quick Preset Increments */}
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-300">
                    <span className="text-gray-400">Quick adjust:</span>
                    <button
                      type="button"
                      aria-label="Increase revenue by 5000"
                      onClick={() => setMonthlyRevenue(prev => prev + 5000)}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-teal-300 transition cursor-pointer"
                    >
                      +5k
                    </button>
                    <button
                      type="button"
                      aria-label="Increase revenue by 10000"
                      onClick={() => setMonthlyRevenue(prev => prev + 10000)}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-teal-300 transition cursor-pointer"
                    >
                      +10k
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease revenue by 5000"
                      onClick={() => setMonthlyRevenue(prev => Math.max(0, prev - 5000))}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-gray-300 transition cursor-pointer"
                    >
                      -5k
                    </button>
                  </div>
                </div>

                {/* Input 2: Monthly Operating Overhead / Burn */}
                <div className="p-4 bg-[#0A0D0E] border border-[#20282B] rounded-2xl space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <label htmlFor="calc-monthly-expense" className="font-semibold text-gray-200 flex items-center gap-1.5 cursor-pointer">
                      <CreditCard size={14} className="text-rose-400" />
                      <span>Monthly Operating Overhead &amp; Burn</span>
                    </label>
                    <span className="text-[11px] font-mono text-rose-300 font-bold">
                      {currencySym}{monthlyExpense.toLocaleString()}
                    </span>
                  </div>

                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-mono font-bold text-gray-300 text-sm">
                      {currencySym}
                    </span>
                    <input
                      id="calc-monthly-expense"
                      name="calcMonthlyExpense"
                      aria-label="Monthly Operating Overhead and Burn"
                      type="number"
                      min="0"
                      step="250"
                      value={monthlyExpense}
                      onChange={(e) => setMonthlyExpense(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full bg-[#14191B] border border-[#2A3438] focus:border-rose-400 rounded-xl pl-9 pr-4 py-2.5 text-sm font-mono font-bold text-white outline-none transition"
                    />
                  </div>

                  {/* Quick Preset Increments */}
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-gray-300">
                    <span className="text-gray-400">Quick adjust:</span>
                    <button
                      type="button"
                      aria-label="Increase operating expenses by 1000"
                      onClick={() => setMonthlyExpense(prev => prev + 1000)}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-rose-300 transition cursor-pointer"
                    >
                      +1k
                    </button>
                    <button
                      type="button"
                      aria-label="Increase operating expenses by 2500"
                      onClick={() => setMonthlyExpense(prev => prev + 2500)}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-rose-300 transition cursor-pointer"
                    >
                      +2.5k
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease operating expenses by 1000"
                      onClick={() => setMonthlyExpense(prev => Math.max(0, prev - 1000))}
                      className="px-2 py-0.5 rounded bg-[#1A2225] hover:bg-[#253034] text-gray-300 transition cursor-pointer"
                    >
                      -1k
                    </button>
                  </div>
                </div>

              </div>

              {/* 🛑 THE VISUAL SHOCK: WHAT HAPPENS WITHOUT RING-FENCING (THE THREAT) */}
              <div className="p-4 sm:p-5 bg-gradient-to-r from-rose-950/30 via-[#121516] to-[#0D1011] border-2 border-rose-500/30 rounded-2xl space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-rose-500/20 pb-3">
                  <div className="flex items-center gap-2 text-rose-400 font-mono font-bold text-xs uppercase tracking-wider">
                    <AlertCircle size={16} className="text-rose-400" />
                    <span>The Cost of Inaction (Without Real-Time Ring-Fencing)</span>
                  </div>
                  <span className="text-[10px] font-mono text-rose-300 bg-rose-500/15 px-2.5 py-0.5 rounded-full border border-rose-500/30">
                    Unhedged Tax Exposure
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 font-mono text-center">
                  <div className="p-3.5 bg-[#0A0D0E] border border-rose-500/20 rounded-xl space-y-1">
                    <span className="text-[10px] text-gray-400 uppercase font-sans block">12-Month Mingled Tax Debt</span>
                    <div className="text-xl sm:text-2xl font-black text-rose-400">
                      -{currencySym}{(calculatedTaxBuffer * 12).toLocaleString()}
                    </div>
                    <span className="text-[10px] text-gray-400 block font-sans">
                      Accidentally spent as operational runway
                    </span>
                  </div>

                  <div className="p-3.5 bg-[#0A0D0E] border border-amber-500/20 rounded-xl space-y-1">
                    <span className="text-[10px] text-gray-400 uppercase font-sans block">Late Filing &amp; Penalty Risk</span>
                    <div className="text-xl sm:text-2xl font-black text-amber-400">
                      -{currencySym}{(Math.round(calculatedTaxBuffer * 1.8)).toLocaleString()}
                    </div>
                    <span className="text-[10px] text-gray-400 block font-sans">
                      Estimated {shortTaxAuthority} audit penalties
                    </span>
                  </div>

                  <div className="p-3.5 bg-[#0A0D0E] border border-rose-500/30 rounded-xl space-y-1">
                    <span className="text-[10px] text-gray-400 uppercase font-sans block">The False Runway Trap</span>
                    <div className="text-xl sm:text-2xl font-black text-rose-300 flex items-center justify-center gap-1">
                      <span>-52%</span>
                      <span className="text-xs text-gray-400 font-sans font-normal">Surprise Drop</span>
                    </div>
                    <span className="text-[10px] text-rose-400 block font-sans">
                      Bank says 5 mos • Real post-tax runway: 2.1 mos
                    </span>
                  </div>
                </div>
              </div>

              {/* 🛡️ THE AZURAOS FORTRESS DEFENSE (THE SOLUTION) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-center">
                
                {/* Metric 1: Operating Profit Margin */}
                <div className="p-4 bg-[#0A0D0E] border border-[#20282B] rounded-2xl space-y-1.5">
                  <div className="flex items-center justify-center gap-1 text-[11px] text-gray-400 uppercase font-sans">
                    <span>Defended Unit Margin</span>
                  </div>
                  <div className={`text-2xl sm:text-3xl font-black ${
                    calculatedMargin >= 55 ? 'text-emerald-400' : calculatedMargin >= 38 ? 'text-[#14B8A6]' : 'text-rose-400'
                  }`}>
                    {calculatedMargin.toFixed(1)}%
                  </div>
                  <div className="w-full bg-[#182023] rounded-full h-1.5 overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        calculatedMargin >= 55 ? 'bg-emerald-400' : calculatedMargin >= 38 ? 'bg-[#14B8A6]' : 'bg-rose-400'
                      }`}
                      style={{ width: `${Math.min(100, calculatedMargin)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-300 block font-sans">
                    (Inflows − Overhead) &divide; Inflows
                  </span>
                </div>

                {/* Metric 2: Statutory Tax Reserve Buffer */}
                <div className="p-4 bg-[#0A0D0E] border border-amber-500/30 rounded-2xl space-y-1.5">
                  <div className="flex items-center justify-center gap-1 text-[11px] text-amber-300 uppercase font-sans font-semibold">
                    <span>Ring-Fenced Buffer ({shortTaxAuthority})</span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-amber-300">
                    {currencySym}{calculatedTaxBuffer.toLocaleString()}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTaxAssumptions(prev => !prev)}
                    className="text-[10px] text-amber-400 hover:text-amber-200 underline block mx-auto font-sans cursor-pointer transition"
                  >
                    {showTaxAssumptions ? 'Hide tax breakdown ▴' : `${currentPreset.vatRate > 0 ? `${currentPreset.vatRate}% VAT + Income Hold` : 'Est. Reserve'} • View details ▾`}
                  </button>
                </div>

                {/* Metric 3: Indicative Distributable Cash */}
                <div className="p-4 bg-gradient-to-br from-[#0A0D0E] to-teal-950/20 border border-[#14B8A6]/50 rounded-2xl space-y-1.5">
                  <span className="text-[11px] text-teal-300 uppercase font-sans font-semibold">True Safe Owner Draw</span>
                  <div className="text-2xl sm:text-3xl font-black text-emerald-400">
                    {currencySym}{calculatedSpendableDividend.toLocaleString()}
                  </div>
                  <span className="text-[10px] text-teal-300 block font-sans">
                    100% protected net spendable cash
                  </span>
                </div>

              </div>

              {/* Tax Calculation Assumptions Drawer */}
              {showTaxAssumptions && (
                <div className="p-4 bg-[#0B0F11] border border-amber-500/30 rounded-2xl space-y-2 text-xs font-sans animate-fade-in text-gray-300">
                  <div className="flex items-center justify-between font-mono text-[11px] text-amber-400 font-bold border-b border-amber-500/20 pb-2">
                    <span>⚖️ Statutory Reserve Assumptions &amp; Model Transparency</span>
                    <span className="text-gray-300 font-normal">{currentPreset.name} Standard Jurisdiction</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
                    <div className="p-2.5 bg-[#121618] rounded-xl border border-[#1F2528] space-y-1 font-mono">
                      <div className="text-[11px] text-gray-300">1. Indirect Tax (VAT / Sales Tax):</div>
                      <div className="text-white font-bold">{currencySym}{taxBreakdown.vatAmount.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-300 font-sans">Assumes {currentPreset.vatRate}% standard rate on gross VAT-inclusive client receipts.</div>
                    </div>
                    <div className="p-2.5 bg-[#121618] rounded-xl border border-[#1F2528] space-y-1 font-mono">
                      <div className="text-[11px] text-gray-300">2. Provisional Income / Corp Hold:</div>
                      <div className="text-amber-300 font-bold">{currencySym}{taxBreakdown.estimatedIncomeTax.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-300 font-sans">Conservative ~18% buffer held aside on net operating cash flow after VAT &amp; overhead.</div>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-300 pt-1 leading-relaxed">
                    <strong>Accounting &amp; Legal Note:</strong> Calculations are indicative operating reserves to protect independent founders from unexpected statutory liability. Whether funds can legally be distributed as dividends depends on your formal entity type (Pty Ltd vs Sole Trader), distributable reserves, and allowable deduction write-offs.
                  </p>
                </div>
              )}

              {/* Dynamic Margin Health Insight Box */}
              <div className="p-4 sm:p-5 bg-[#090C0D] border border-[#1F2528] rounded-2xl space-y-2">
                <div className="flex items-center justify-between text-[11px] font-mono">
                  <span className="text-teal-400 font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <Sparkles size={13} />
                    <span>Live Forward Solvency Insight</span>
                  </span>
                  <span className="text-gray-300">
                    {currentPreset.name} Standard Jurisdiction
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-sans">
                  {marginHealthInsight}
                </p>
              </div>

              {/* Dual Action Bridge: Download Audit OR Launch Workspace */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#1F2528]">
                <button
                  type="button"
                  onClick={() => setIsDiagnosticModalOpen(true)}
                  className="w-full sm:w-auto flex-1 bg-[#1A2326] hover:bg-[#232F34] text-teal-300 hover:text-white border border-[#14B8A6]/40 hover:border-[#14B8A6] font-bold rounded-xl h-11 px-5 flex items-center justify-center gap-2 transition text-xs cursor-pointer shadow-sm"
                >
                  <Download size={14} className="text-teal-400" />
                  <span>Download 1-Page Solvency Audit Pack (PDF)</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleLaunchTrial()}
                  className="w-full sm:w-auto flex-1 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl h-11 px-5 flex items-center justify-center gap-2 transition shadow-md shadow-[#14B8A6]/20 text-xs cursor-pointer active:scale-95"
                >
                  <span>Launch AzuraOS (7-Day Trial)</span>
                  <ArrowRight size={14} />
                </button>
              </div>

            </div>
          </section>

          {/* 📄 3. THE 1-CLICK CPA, INVESTOR & GRANT AUDIT-PACK (THE CORE PRODUCT OUTPUT) */}
          <section className="bg-gradient-to-br from-[#121618] to-[#0A0D0E] border-2 border-[#14B8A6]/30 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl text-left">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-4">
              <div>
                <span className="text-[11px] font-mono font-bold text-teal-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText size={14} />
                  <span>The Core Deliverable • Compliance-As-A-Service</span>
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  The 1-Click CPA, Investor &amp; Grant Audit-Pack
                </h3>
              </div>
              <span className="text-[10px] font-mono text-teal-300 bg-teal-500/15 px-3 py-1 rounded-full border border-teal-500/30 shrink-0">
                Audit Ready in &lt; 60 Seconds
              </span>
            </div>

            <div className="p-3.5 rounded-2xl bg-[#090C0D] border border-teal-500/25 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-teal-500/10 text-teal-400 font-bold font-mono text-sm shrink-0">
                💡
              </span>
              <p className="text-xs text-gray-300 font-mono leading-snug">
                <strong className="text-white">&ldquo;Raised $1M? That&rsquo;s not earned revenue&mdash;that&rsquo;s a ticking runway clock. Made $50k? 30% belongs to the government.&rdquo;</strong>
                <span className="text-gray-400 block sm:inline sm:ml-1 font-sans">AzuraOS replaces spreadsheet chasing with verifiable, audit-ready fiscal packs in 60 seconds.</span>
              </p>
            </div>

            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light max-w-3xl">
              AzuraOS sits <strong>upstream of accounting software</strong>. Rather than spending weekends manually cleaning up messy spreadsheets or chasing missing receipts, AzuraOS compiles your verified gross billings, statutory tax escrows, and bank reconciliation stamps into a single, tamper-evident fiscal audit pack.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              
              {/* Output 1 */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2 hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-2 text-[#14B8A6] font-bold">
                  <Landmark size={16} />
                  <span>1. Structured CPA Balance Ledger</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  1-Click export in structured JSON and CSV formats. Clear separation of gross revenues, indirect taxes (VAT/GST), and operating overhead for seamless accountant handoffs.
                </p>
              </div>

              {/* Output 2 */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2 hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-2 text-purple-400 font-bold">
                  <Globe size={16} />
                  <span>2. Multi-Jurisdiction Nil Return Schedule</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  Pre-calibrated filing schedules for US (IRS Schedule C), UK (HMRC VAT), South Africa (SARS Nil Returns), EU (VAT), Australia (BAS), and India (GST/44ADA).
                </p>
              </div>

              {/* Output 3 */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2 hover:border-teal-500/40 transition-colors">
                <div className="flex items-center gap-2 text-emerald-400 font-bold">
                  <ShieldCheck size={16} />
                  <span>3. Verified Solvency Certificate</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  Non-custodial proof of solvency and cash runway certificate formatted specifically for Venture Capitalists, Accelerator Evaluators, and Commercial SME Lenders.
                </p>
              </div>

            </div>
          </section>

          {/* ⚡ 4. THE 0.05s AIR-GAPPED STATEMENT PARSER (THE SCALPEL) */}
          <section className="space-y-6 text-left">
            <div className="text-center space-y-2 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-300 rounded-full border border-amber-500/25 text-[11px] font-mono font-bold uppercase tracking-wider">
                <Receipt size={13} className="text-amber-400" />
                <span>Air-Gapped Banking Engine</span>
              </div>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white tracking-tight">
                0.05-Second Bank Statement Ingestion
              </h2>
              <p className="text-xs sm:text-sm text-gray-300">
                <strong>Deliberate No-Plaid Architecture:</strong> We never ask for or scrape your live bank passwords. Simply drop your bank PDF, CSV, OFX, or paste raw text. Parsed directly in local browser memory.
              </p>
            </div>

            {/* Interactive Ingestion Simulator Window */}
            <div className="bg-[#121517] border border-[#2A3438] rounded-3xl p-5 sm:p-7 shadow-xl space-y-5">
              
              {/* ⚡ VIRAL UNGATED 1-CLICK PERSONA PRESET SELECTORS */}
              <div className="p-3.5 bg-[#090C0D] border border-amber-500/30 rounded-2xl space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-xs font-mono font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Zap size={14} className="text-amber-400" />
                    <span>1-Click Interactive Demo (Zero Upload / Account Needed)</span>
                  </span>
                  <span className="text-[11px] font-mono text-emerald-400 font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span>Parsed in {sampleScanResult?.latencyMs || 48}ms locally</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono">
                  <button
                    type="button"
                    onClick={() => handleSelectSamplePersona('consulting')}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      sampleScanResult?.persona.includes('Advisory')
                        ? 'bg-[#182327] border-teal-500 text-teal-300 shadow-sm'
                        : 'bg-[#0E1214] border-[#222C30] text-gray-400 hover:text-white'
                    }`}
                  >
                    <div className="font-bold text-white text-[11px]">💼 Advisory Consultant</div>
                    <div className="text-[10px] text-gray-400">$19,450 wire • Retainer &amp; Ops</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelectSamplePersona('saas')}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      sampleScanResult?.persona.includes('Micro-SaaS')
                        ? 'bg-[#182327] border-teal-500 text-teal-300 shadow-sm'
                        : 'bg-[#0E1214] border-[#222C30] text-gray-400 hover:text-white'
                    }`}
                  >
                    <div className="font-bold text-white text-[11px]">💻 Micro-SaaS Operator</div>
                    <div className="text-[10px] text-gray-400">$12,500 MRR • Cloud burn</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelectSamplePersona('agency')}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      sampleScanResult?.persona.includes('Studio')
                        ? 'bg-[#182327] border-teal-500 text-teal-300 shadow-sm'
                        : 'bg-[#0E1214] border-[#222C30] text-gray-400 hover:text-white'
                    }`}
                  >
                    <div className="font-bold text-white text-[11px]">🎨 Creative Agency</div>
                    <div className="text-[10px] text-gray-400">$27,800 milestone • Contractor split</div>
                  </button>
                </div>

                {/* Live Diagnostic Output Box */}
                {sampleScanResult && (
                  <div className="pt-2 border-t border-[#1C2428] grid grid-cols-2 sm:grid-cols-4 gap-2 text-center font-mono">
                    <div className="p-2 bg-[#121618] rounded-lg border border-[#1E2629]">
                      <span className="text-[10px] text-gray-400 block">Gross Inflows</span>
                      <strong className="text-white text-xs">{currencySym}{sampleScanResult.gross.toLocaleString()}</strong>
                    </div>
                    <div className="p-2 bg-[#121618] rounded-lg border border-amber-500/20">
                      <span className="text-[10px] text-amber-300 block">Tax Buffer Ring-Fenced</span>
                      <strong className="text-amber-400 text-xs">-{currencySym}{sampleScanResult.taxBuffer.toLocaleString()}</strong>
                    </div>
                    <div className="p-2 bg-[#121618] rounded-lg border border-rose-500/20">
                      <span className="text-[10px] text-rose-300 block">Committed Burn</span>
                      <strong className="text-rose-400 text-xs">-{currencySym}{sampleScanResult.overhead.toLocaleString()}</strong>
                    </div>
                    <div className="p-2 bg-gradient-to-br from-[#121618] to-teal-950/30 rounded-lg border border-teal-500/40">
                      <span className="text-[10px] text-teal-300 block">True Safe Draw</span>
                      <strong className="text-emerald-400 text-xs">{currencySym}{sampleScanResult.spendable.toLocaleString()}</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* Format Mode Switcher */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-3">
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-1.5 w-full sm:w-auto">
                  {(['csv', 'pdf', 'paste', 'manual'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleTriggerReconcilerSim(mode)}
                      className={`px-2.5 sm:px-3 py-2 sm:py-1.5 rounded-xl text-xs font-mono font-bold transition flex items-center justify-center gap-1.5 cursor-pointer text-center ${
                        reconcilerMode === mode
                          ? 'bg-amber-400 text-black shadow-sm'
                          : 'bg-[#0A0D0E] text-gray-400 border border-[#20282B] hover:text-white'
                      }`}
                    >
                      <span>{mode === 'csv' ? '⚡ CSV File' : mode === 'pdf' ? '📄 PDF Statement' : mode === 'paste' ? '📋 Paste Lines' : '✍️ Direct Entry'}</span>
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-400 shrink-0">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>Parsed in 0.05s • Zero Cloud Transmission</span>
                </div>
              </div>

              {/* Parsed Transactions Sample Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-mono">
                  <thead>
                    <tr className="border-b border-[#1F2528] text-gray-400 text-[10px] uppercase">
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3">Transaction Description</th>
                      <th className="py-2 px-3">Income Stream</th>
                      <th className="py-2 px-3">Amount</th>
                      <th className="py-2 px-3 text-right">Tax Deduction Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#182023] text-gray-300">
                    {reconcilerDataByMode[reconcilerMode].map((item) => (
                      <tr key={item.id} className="hover:bg-[#151B1D]/60 transition">
                        <td className="py-2.5 px-3 text-gray-400">{item.date}</td>
                        <td className="py-2.5 px-3 font-medium text-white">{item.desc}</td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded bg-[#172023] text-teal-300 text-[10px] border border-[#243237]">
                            {item.stream}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-bold">
                          <span className={item.type === 'credit' ? 'text-emerald-400' : 'text-rose-400'}>
                            {item.type === 'credit' ? '+' : '-'}{currencySym}{item.amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                            <Check size={10} /> {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-3 bg-[#0A0D0E] border border-[#1F2528] rounded-xl flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="text-gray-400">
                  💡 <strong>Human-in-the-Loop Review:</strong> AzuraOS never silently commits transactions without founder verification.
                </span>
                {onOpenReconciler && (
                  <button
                    type="button"
                    onClick={onOpenReconciler}
                    className="text-amber-400 hover:text-white font-mono text-xs font-bold underline cursor-pointer"
                  >
                    Launch Full Reconciler Suite →
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* 🛡️ 5. THE POST-SAAS ARCHITECTURE & SECURITY MOATS */}
          <section className="bg-gradient-to-br from-[#121618] to-[#0A0D0E] border border-[#14B8A6]/30 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl text-left">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-4">
              <div>
                <span className="text-[11px] font-mono font-bold text-teal-400 uppercase tracking-wider">
                  Technical Due Diligence &amp; Moats
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  Why AzuraOS Sits Upstream of Accounting Software
                </h3>
              </div>
              <button
                type="button"
                onClick={() => handleOpenWhatIsAzuraOS('capabilities')}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#172023] hover:bg-[#202C30] border border-teal-500/30 text-teal-300 hover:text-white text-xs font-bold transition cursor-pointer"
              >
                <span>Full Platform Architecture (8 Workspaces)</span>
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              
              {/* Moat 1: Dual-Vault */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-[#14B8A6] font-bold">
                  <Lock size={15} />
                  <span>Dual-Vault Cryptography</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  Client-side AES-256-GCM encryption with PBKDF2 (100,000 rounds). In air-gapped mode, zero network writes are dispatched. In cloud sync mode, Firestore receives indecipherable opaque ciphertext.
                </p>
              </div>

              {/* Moat 2: Zero AI Model Training */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-purple-400 font-bold">
                  <Bot size={15} />
                  <span>Zero AI Model Training Guarantee</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  Powered by private Gemini API keys (BYOK) under Google Cloud enterprise terms. Your sensitive client contracts and financial records are mathematically never used to train public foundation models.
                </p>
              </div>

              {/* Moat 3: 50% Milestone Invoicing */}
              <div className="p-4 bg-[#0B0E0F] border border-[#1F2528] rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 font-bold">
                  <FileCheck size={15} />
                  <span>Contract-to-Settlement Pipeline</span>
                </div>
                <p className="text-gray-300 leading-relaxed text-[11px]">
                  Unifies Client Booking (`/book`), E-Sign Service Agreements, and Enforceable 50% Milestone Invoices into one unbroken custody chain—eliminating payment defaults before work begins.
                </p>
              </div>

            </div>

            {/* Verified Ecosystem Credentials */}
            <div className="pt-2 flex flex-wrap items-center justify-between gap-3 text-xs border-t border-[#1A2225]">
              <GoogleCloudPartnerBadge variant="compact" />
              <div className="flex items-center gap-2">
                <a
                  href="/architecture.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white font-mono text-[11px] underline flex items-center gap-1"
                >
                  <span>Formal Architecture Whitepaper</span>
                  <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </section>

          {/* 🏛️ 6. INSTITUTIONAL COHORT LICENSING & ACCELERATOR PILOT PACKS (PRIMARY PRICING) */}
          <section className="bg-[#121517] border-2 border-purple-500/40 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl text-left">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-4">
              <div>
                <span className="text-[11px] font-mono font-bold text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Building2 size={15} />
                  <span>Institutional Licensing &amp; Accelerator Deployment</span>
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white">Cohort Pilot Packs &amp; Enterprise ESD Tiers</h3>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-mono font-bold">
                <Check size={13} />
                <span>Turnkey Batch Rollout</span>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light max-w-3xl">
              Accelerators, Incubators, and Enterprise Supplier Development (ESD) programs license AzuraOS to standardize financial hygiene, ring-fence batch taxes, and export verified grant audit packs across their entire portfolio.
            </p>

            {/* Institutional Cohort Tier Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono">
              
              {/* 10 Seats */}
              <div className="p-5 bg-[#090C0D] border border-[#20282B] rounded-2xl space-y-3 flex flex-col justify-between hover:border-purple-500/40 transition">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-purple-300 uppercase">Starter Cohort</span>
                    <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 text-[10px]">10 Seats</span>
                  </div>
                  <div className="text-xl font-black text-white">$2,400 <span className="text-xs text-gray-400 font-sans font-normal">/ batch</span></div>
                  <ul className="text-xs text-gray-400 space-y-1.5 font-sans pt-1">
                    <li className="flex items-center gap-1.5 text-gray-300">✓ Turnkey 10-founder onboarding</li>
                    <li className="flex items-center gap-1.5 text-gray-300">✓ 6-Jurisdiction tax engines</li>
                    <li className="flex items-center gap-1.5 text-gray-300">✓ 1-Click CPA/Grant audit pack</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDemoInquiryType('accelerator_pilot');
                    setIsRequestDemoOpen(true);
                  }}
                  className="w-full py-2 bg-[#1A2225] hover:bg-purple-600 text-purple-300 hover:text-white font-bold text-xs rounded-xl transition cursor-pointer"
                >
                  Request 10-Seat Pack
                </button>
              </div>

              {/* 25 Seats - Popular */}
              <div className="p-5 bg-gradient-to-b from-[#161420] to-[#0D0B12] border-2 border-purple-500 rounded-2xl space-y-3 flex flex-col justify-between shadow-xl relative">
                <div className="absolute -top-2.5 right-4 px-2.5 py-0.5 bg-purple-600 text-white text-[9px] font-bold font-mono uppercase rounded-full tracking-wider shadow">
                  Most Popular Batch
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-purple-300 uppercase">Growth Accelerator</span>
                    <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px]">25 Seats</span>
                  </div>
                  <div className="text-xl font-black text-white">$5,500 <span className="text-xs text-gray-400 font-sans font-normal">/ batch</span></div>
                  <ul className="text-xs text-gray-400 space-y-1.5 font-sans pt-1">
                    <li className="flex items-center gap-1.5 text-gray-200">✓ Standardized 25-founder batch setup</li>
                    <li className="flex items-center gap-1.5 text-gray-200">✓ Live cohort runway &amp; solvency radar</li>
                    <li className="flex items-center gap-1.5 text-gray-200">✓ Dedicated sponsor audit review desk</li>
                    <li className="flex items-center gap-1.5 text-gray-200">✓ Custom co-branding &amp; reporting</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDemoInquiryType('accelerator_pilot');
                    setIsRequestDemoOpen(true);
                  }}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs rounded-xl transition shadow-md cursor-pointer"
                >
                  Reserve 25-Seat Pilot
                </button>
              </div>

              {/* 50 to 100+ Seats */}
              <div className="p-5 bg-[#090C0D] border border-[#20282B] rounded-2xl space-y-3 flex flex-col justify-between hover:border-purple-500/40 transition">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-purple-300 uppercase">Enterprise ESD &amp; Bank</span>
                    <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 text-[10px]">50–100+ Seats</span>
                  </div>
                  <div className="text-xl font-black text-white">Custom Volume <span className="text-xs text-gray-400 font-sans font-normal">/ annual</span></div>
                  <ul className="text-xs text-gray-400 space-y-1.5 font-sans pt-1">
                    <li className="flex items-center gap-1.5 text-gray-300">✓ Multi-batch enterprise rollout</li>
                    <li className="flex items-center gap-1.5 text-gray-300">✓ Portfolio credit risk mitigation logs</li>
                    <li className="flex items-center gap-1.5 text-gray-300">✓ API webhooks &amp; SSO integration</li>
                    <li className="flex items-center gap-1.5 text-gray-300">✓ POPIA/GDPR audit pack</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDemoInquiryType('bank_pilot');
                    setIsRequestDemoOpen(true);
                  }}
                  className="w-full py-2 bg-[#1A2225] hover:bg-purple-600 text-purple-300 hover:text-white font-bold text-xs rounded-xl transition cursor-pointer"
                >
                  Request Enterprise Pilot
                </button>
              </div>

            </div>

            {/* 👤 SECONDARY ACTION: ELEGANT SINGLE-OPERATOR SELF-SERVE PASS */}
            <div className="p-4 rounded-2xl bg-[#0B0E0F] border border-[#222B2E] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="space-y-0.5 text-center sm:text-left">
                <strong className="text-white text-xs block">Independent Consultant or Solo Founder?</strong>
                <p className="text-[11px] text-gray-400">
                  Access all 8 workspaces with our single-seat 7-Day Unrestricted Pass ($29/mo self-serve).
                </p>
              </div>
              <div className="flex flex-col items-center sm:items-end gap-1">
                <button
                  type="button"
                  onClick={() => handleLaunchTrial()}
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl transition shadow-md flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-95"
                >
                  <span>Start 7-Day Single Pass ($29/mo)</span>
                  <ArrowRight size={13} />
                </button>
                <span className="text-[10px] text-gray-500 font-medium text-center sm:text-right">
                  Billed monthly after trial ($29/mo). Cancel anytime in 1-click. 14-day refund guarantee.
                </span>
              </div>
            </div>
          </section>
        </>
      )}  {/* ⚖️ 7. FIDUCIARY DISCLAIMER & STATUTORY AIRLOCK (INDEMNITY SAFEGUARD) */}
      <section className="p-4 sm:p-5 bg-[#090C0D] border border-amber-500/25 rounded-3xl flex items-start gap-3 text-xs font-mono shadow-md">
        <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
          <Scale size={16} />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-amber-300 uppercase tracking-wider text-[11px]">
              Fiduciary &amp; Cross-Border Statutory Airlock
            </span>
            <span className="text-[10px] text-gray-400">• NFA Protective Notice</span>
          </div>
          <p className="text-gray-300 leading-relaxed text-[11px] font-sans">
            AzuraOS generates indicative operating cash flow models and automated holding buffers strictly from standard jurisdiction presets (IRS, HMRC, SARS, EU VAT, ATO, GST). It does not provide certified legal, chartered tax, or formal investment advisory services. Designed to prevent catastrophic co-mingling tax spend and generate accountant-ready audit exports.
          </p>
        </div>
      </section>

      {/* 🧭 8. FOOTER & MODAL TRIGGERS */}
      <footer className="pt-4 pb-8 border-t border-[#1F2528] text-center space-y-4 text-xs text-gray-400">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-gray-300">
          <button type="button" onClick={() => handleOpenWhatIsAzuraOS('pitch')} className="hover:text-white cursor-pointer transition">
            What is AzuraOS
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenSecurity} className="hover:text-white cursor-pointer transition">
            System Architecture
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenSecurity} className="hover:text-white cursor-pointer transition">
            Security Specifications
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenPrivacy} className="hover:text-white cursor-pointer transition">
            Privacy Policy
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenTerms} className="hover:text-white cursor-pointer transition">
            Terms of Service
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenRefundPolicy} className="hover:text-white cursor-pointer transition">
            Refunds
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenFAQ} className="hover:text-white cursor-pointer transition">
            FAQ
          </button>
          <span>•</span>
          <button type="button" onClick={onOpenContact} className="hover:text-white cursor-pointer transition">
            Contact Support
          </button>
        </div>

        <p className="text-[11px] text-gray-400">
          &copy; 2026 <strong>AzuraOS</strong> is a proprietary sovereign product of <strong>Azura Seraph™ (Pty) Ltd</strong> (Reg: 2024/560516/07), South Africa.
        </p>
      </footer>

      {/* 🌟 VIP DEMO & PILOT REQUEST MODAL */}
      <RequestDemoModal
        isOpen={isRequestDemoOpen}
        onClose={() => setIsRequestDemoOpen(false)}
        defaultInquiryType={demoInquiryType}
        onOpenInteractiveTour={() => {
          setIsRequestDemoOpen(false);
          setIsInteractiveTourOpen(true);
        }}
      />

      {/* 🚀 INTERACTIVE 60-SECOND PRODUCT TOUR MODAL */}
      <InteractiveTourModal
        isOpen={isInteractiveTourOpen}
        onClose={() => toggleInteractiveTour(false)}
        onStartTrial={() => {
          toggleInteractiveTour(false);
          handleLaunchTrial();
        }}
        onRequestVipDemo={() => {
          toggleInteractiveTour(false);
          setDemoInquiryType('vip_1on1');
          setIsRequestDemoOpen(true);
        }}
      />

      {/* 💡 WHAT IS AZURAOS MODAL (ARCHITECTURAL DEEP-DIVE & MATRIX) */}
      <WhatIsAzuraOSModal
        isOpen={isWhatIsAzuraOSModalOpen}
        onClose={() => setIsWhatIsAzuraOSModalOpen(false)}
        initialTab={whatIsAzuraOSTab}
        onStartTrial={() => {
          setIsWhatIsAzuraOSModalOpen(false);
          handleLaunchTrial();
        }}
        onOpenTour={() => {
          setIsWhatIsAzuraOSModalOpen(false);
          toggleInteractiveTour(true);
        }}
        onRequestDemo={() => {
          setIsWhatIsAzuraOSModalOpen(false);
          setDemoInquiryType('vip_1on1');
          setIsRequestDemoOpen(true);
        }}
      />

      {/* 📄 MARGIN DIAGNOSTIC LEAD CAPTURE & REPORT MODAL */}
      <MarginDiagnosticEmailModal
        isOpen={isDiagnosticModalOpen}
        onClose={() => setIsDiagnosticModalOpen(false)}
        data={diagnosticPayload}
        onContinueToTrial={(email) => {
          setIsDiagnosticModalOpen(false);
          handleLaunchTrial(email);
        }}
        showToast={showToast}
      />

      {/* ⚡ 10-SECOND QUICK START WIZARD */}
      <QuickStartWizard
        isOpen={isQuickStartOpen}
        onClose={() => setIsQuickStartOpen(false)}
        onComplete={(mode) => {
          setIsQuickStartOpen(false);
          try {
            localStorage.setItem('azura_workspace_mode', mode);
          } catch {}
          onGetStarted();
        }}
        showToast={showToast}
      />

    </div>
  );
}
